import React, { useCallback, useState, useMemo, useEffect } from "react";
import { observer } from "mobx-react";
import { useStore, useDebounce } from "../../../../../hooks";
import { useMediaQueries } from "../../../../../utils/mediaQueries";
import {
  StyledContainer,
  TableWrapper,
  BreadCrumbContainer,
  PageContainer,
  SearchAndButtonContainer,
  SearchContainer,
  CustomSearchInput,
  InputField,
  SearchIcon,
  ButtonWrapper,
  FilterWrapper,
  TruncatedText,
  SharedAgentsBadge,
} from "./Vault.styled";
import { getDensity } from "../../../../../utils/GetDensity/GetDensity";
import DynamicBreadcrumbs from "../../../../../elements/v2/DynamicBreadcrumbs/DynamicBreadcrumbs";
import { generateColumnTable } from "../../../../../utils/tableUtils";
import { Icon, RoundedButton } from "../../../../../elements";
import Alerts from "../../../../../elements/v2/Alerts/Alerts";
import SaveSingleContactModal from "../../../../../elements/v2/SaveSingleContactModal/SaveSingleContactModal";
import SmartTableContainer from "../../../../../elements/v2/SmartTableContainer/SmartTableContainer";
import AddIcon from "@mui/icons-material/Add";
import VaultDocument from "../../../VaultPage/components/VaultDocument/VaultDocument";
import FilterSelector from "../../../../../elements/v2/FilterSelector/FilterSelector";
import { commonFilterProps } from "../../../AsistantManagement/consts";
import { useNavigate, useSearchParams } from "react-router-dom";
import { vaultStore } from "../../store/VaultStore";
import UploadModal from "../../components/UploadModal/UploadModal";
import { StatusFilterValues } from "../../enums/StatusFilterEnum";
import timeAgo from "../../../../../utils/timeAgo";
import { DISPLAY_MODE } from "../../../../../components/app/consts";
import RenameVaultModal from "../../components/RenameVaultModal/RenameVaultModal";
import ArchiveVaultModal from "../../components/ArchiveVaultModal/ArchiveVaultModal";
import { statusColumn } from "../../components/VaultBadgeStatus/VaultBadgeStatus";
import ShareVaultModal from "../../components/ShareVaultModal";
import MenuButtonWrapper from "../../../../../elements/v2/MenuButtonWrapper";

const VaultPage = observer(() => {
  const { authStore } = useStore();
  const { isMobile, isTablet, isDesktop } = useMediaQueries();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const [, setGlobalFilter] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const [sorting, setSorting] = useState([{ id: "name", desc: false }]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 15,
  });
  const [createContactModal, setCreateContactModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const density = useMemo(
    () => getDensity(isMobile, isTablet, isDesktop),
    [isMobile, isTablet, isDesktop]
  );
  const [ownerFilter, setOwnerFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [modifiedFilter, setModifiedFilter] = useState([]);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [selectedVault, setSelectedVault] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);

  const modeTable = searchParams.get('mode') || DISPLAY_MODE.TABLE;

  const ownerOptions = useMemo(() => {
    if (!authStore.users) return [];
    return authStore.users.map(user => ({
      value: user.id,
      label: `${user.first_name} ${user.last_name || ''}`.trim()
    }));
  }, [authStore.users]);

  useEffect(() => {
    setGlobalFilter(debouncedSearchValue);
  }, [debouncedSearchValue]);

  useEffect(() => {
    if (authStore.selectedAccount?.id) {
      void vaultStore.fetchDocumentFolders(
        authStore.selectedAccount.id, 
        debouncedSearchValue, 
        statusFilter[0],
        ownerFilter[0] || ''
      ).finally(() => {
        setIsInitialLoading(false);
      });
    }
  }, [authStore.selectedAccount.id, debouncedSearchValue, statusFilter, ownerFilter]);

  // Ordenar los items para que los archivados aparezcan al final
  const sortedData = useMemo(() => {
    if (!vaultStore.data) return [];
    return [...vaultStore.data].sort((a, b) => {
      if (a.status === 'archived' && b.status !== 'archived') return 1;
      if (a.status !== 'archived' && b.status === 'archived') return -1;
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vaultStore.data]);

  const handleSearchChange = useCallback((value) => {
    setSearchValue(value);
  }, []);

  const handleFilterChange = useCallback(
    (filterType) => (event) => {
      const newValue =
        typeof event.target.value === "string"
          ? event.target.value.split(",")
          : event.target.value;

      const filterConfigs = {
        owner: {
          setState: setOwnerFilter,
        },
        status: {
          setState: setStatusFilter,
        },
        modified: {
          setState: setModifiedFilter,
        },
      };

      const config = filterConfigs[filterType];
      if (config) {
        config.setState(newValue);
      }
    },
    []
  );

  const handleClearAll = useCallback(() => {
    setOwnerFilter([]);
    setStatusFilter([]);
    setModifiedFilter([]);
  }, []);

  const filterSelectorsConfig = useMemo(
    () => [
      {
        title: "Owner",
        data: ownerOptions,
        minWidthForm: 103,
        minWidthPaper: 263,
        value: ownerFilter,
        filterType: "owner",
        isObjectData: true,
        type: "single",
      },
      {
        title: "Status",
        data: StatusFilterValues,
        minWidthForm: 102,
        minWidthPaper: 224,
        value: statusFilter,
        filterType: "status",
        type: "single",
      },
      {
        title: "Last Updated",
        data: ["Last 24 hours", "Last 7 days", "Last 30 days", "Last 90 days"],
        minWidthForm: 158,
        minWidthPaper: 224,
        value: modifiedFilter,
        filterType: "modified",
        type: "single",
      },
    ],
    [ownerOptions, ownerFilter, statusFilter, modifiedFilter]
  );

  const columns = [
    {
      ...generateColumnTable("name", "Name"),
      Cell: ({ cell }) => (
        <TruncatedText title={cell.getValue() || "-"}>
          {cell.getValue() || "-"}
        </TruncatedText>
      ),
    },
    {
      ...generateColumnTable("description", "Description"),
      Cell: ({ cell }) => (
        <TruncatedText title={cell.getValue() || "-"}>
          {cell.getValue() || "-"}
        </TruncatedText>
      ),
    },
    {
      ...generateColumnTable("metadata.shared_with_count", "Shared agents"),
      Cell: ({ cell }) => (
        <SharedAgentsBadge>
          <Icon name="agentShare" height="14px" width="14px" /> {cell.getValue() || "0"} {`+`}
        </SharedAgentsBadge>
      ),
    },
    statusColumn,
    {
      ...generateColumnTable("latest_updated", "Last Updated"),
      Cell: ({ cell }) => {
        const value = cell.getValue();
        return value ? timeAgo(value) : "-";
      },
    },
    {
      ...generateColumnTable("created_by", "Created By"),
      Cell: ({ cell }) => {
        const userId = cell.getValue();
        const user = authStore.users?.find(user => user.id === userId);
        return user ? `${user.first_name} ${user.last_name || ''}`.trim() : '-';
      },
    },
    {
      ...generateColumnTable("files_stored", "Files"),
      Cell: ({ cell }) => cell.getValue() || "-",
    },
    {
      ...generateColumnTable("actions", "Actions"),
      Cell: ({ row }) => {
        const rowData = row.original;
        return (
          <MenuButtonWrapper items={getMenuItems(rowData)} />
        );
      },
      muiTableBodyCellProps: {
        sx: {
          position: 'relative',
          padding: '16px !important',
        },
      },
    },
  ];

  const breadcrumbItems = [{ label: "Vault", path: "/v2/vault" }];

  const handleUploadSubmit = () => {
    setShowUploadModal(false);
    setShowSuccessAlert(true);
  };

  const handleRowClick = useCallback((row) => {
    if (showShareModal || showRenameModal || showArchiveModal || showUploadModal) {
      return;
    }

    const folderId = encodeURIComponent(row.id);
    const customerId = authStore.selectedAccount?.id;
    const vaultName = encodeURIComponent(row.name);
    const currentMode = searchParams.get('mode') || DISPLAY_MODE.TABLE;
    navigate(`/v2/vault/documents?id=${folderId}&customerId=${customerId}&vaultName=${vaultName}&mode=${currentMode}`);
  }, [navigate, authStore.selectedAccount, searchParams, showShareModal, showRenameModal, showArchiveModal, showUploadModal]);

  const handleModeTableChange = useCallback((mode) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('mode', mode);
    setSearchParams(newSearchParams);
  }, [searchParams, setSearchParams]);

  const handleRename = () => {
    setShowRenameModal(false);
    if (authStore.selectedAccount?.id) {
      void vaultStore.fetchDocumentFolders(
        authStore.selectedAccount.id,
        debouncedSearchValue,
        statusFilter[0],
        ownerFilter[0] || ''
      );
    }
  };

  const handleArchive = () => {
    setShowArchiveModal(false);
  };

  const getMenuItems = (rowData) => [
    { 
      label: 'Rename Vault', 
      onClick: () => {
        setSelectedVault({
          id: rowData.id,
          name: rowData.name,
          description: rowData.description,
          created_by: rowData.created_by,
          files_stored: rowData.files_stored
        });
        setShowRenameModal(true);
      }
    },
    { 
      label: 'Manage Access', 
      onClick: () => console.log('Manage Access clicked')
    },
    { 
      label: 'Share', 
      onClick: () => {
        setSelectedVault(rowData);
        setShowShareModal(true);
      }
    },
    { 
      label: 'Archive Vault', 
      onClick: () => {
        setSelectedVault(rowData);
        setShowArchiveModal(true);
      }
    },
    { 
      label: 'View Activity Log', 
      onClick: () => console.log('View Activity Log clicked')
    }
  ];

  return (
    <PageContainer>
      {vaultStore.error.show && (
        <Alerts
          severity={vaultStore.error.severity}
          title={vaultStore.error.title}
          message={vaultStore.error.message}
          onClose={() => vaultStore.closeAlert()}
        />
      )}

      {showSuccessAlert && (
        <Alerts
          severity="success"
          title="Vault created successfully"
          message="Your vault has been successfully created and is ready for use. You can now store and manage your files securely!"
          onClose={() => setShowSuccessAlert(false)}
        />
      )}

      <BreadCrumbContainer>
        <DynamicBreadcrumbs items={breadcrumbItems} currentPath="/vault" />
      </BreadCrumbContainer>

      <StyledContainer>
        <SearchAndButtonContainer>
          <SearchContainer>
            <CustomSearchInput>
              <SearchIcon>
                <Icon name="magnifier" height="20px" width="20px" />
              </SearchIcon>
              <InputField
                type="text"
                value={searchValue}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search Vaults by name or keyword"
              />
            </CustomSearchInput>
            <FilterWrapper>
              {filterSelectorsConfig.map((config) => (
                <FilterSelector
                  key={config.title}
                  {...commonFilterProps()}
                  {...config}
                  handleChange={handleFilterChange(config.filterType)}
                />
              ))}
              <RoundedButton
                width="86px"
                height="40px"
                kind="confirm"
                outline={true}
                fontWeight="normal"
                fontSize="14px"
                margin="0"
                borderRadius="8px"
                onClick={handleClearAll}
              >
                Clear all
              </RoundedButton>
            </FilterWrapper>
          </SearchContainer>
          <ButtonWrapper>
            <SmartTableContainer.ModeSwitcher
              mode={modeTable}
              setMode={handleModeTableChange}
            />
            <RoundedButton kind="blue" onClick={() => setShowUploadModal(true)} height="40px" style={{ paddingRight: "16px"}}>
              <AddIcon />
              Create
            </RoundedButton>
          </ButtonWrapper>
        </SearchAndButtonContainer>

        <TableWrapper>
          <SmartTableContainer
            columns={columns}
            data={sortedData}
            rowCount={vaultStore.totalRowCount}
            pagination={pagination}
            setPagination={setPagination}
            sorting={sorting}
            setSorting={setSorting}
            isLoading={isInitialLoading || vaultStore.isLoading}
            enableRowSelection={false}
            density={density}
            mode={modeTable}
            itemToRender={<VaultDocument />}
            onRowClick={handleRowClick}
            onItemClick={handleRowClick}
            showEmptyData={
              !isInitialLoading && (
                <SmartTableContainer.EmptyState
                  title="No vaults found"
                  description="You haven't created any vaults yet. To manage your files securely, please create a vault first."
                />
              )
            }
            columnsGrid={3}
          />
        </TableWrapper>

        <SaveSingleContactModal
          createContactModal={createContactModal}
          setCreateContactModal={setCreateContactModal}
        />

        <UploadModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onSubmit={handleUploadSubmit}
        />

        <RenameVaultModal
          isOpen={showRenameModal}
          onClose={() => setShowRenameModal(false)}
          onSubmit={handleRename}
          currentName={selectedVault?.name}
          description={selectedVault?.description}
          fileId={selectedVault?.id}
          created_by={selectedVault?.created_by}
          files_stored={selectedVault?.files_stored}
        />

        <ArchiveVaultModal
          isOpen={showArchiveModal}
          onClose={() => setShowArchiveModal(false)}
          onSubmit={handleArchive}
          currentName={selectedVault?.name}
          description={selectedVault?.description}
          fileId={selectedVault?.id}
          created_by={authStore.userId}
          files_stored={selectedVault?.files_stored}
        />

        <ShareVaultModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          vaultId={selectedVault?.id}
          vaultName={selectedVault?.name}
        />
      </StyledContainer>
    </PageContainer>
  );
});

export default VaultPage;
